import { LiveAnnouncer } from '@angular/cdk/a11y';
import { inject, Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { captureException } from '@sentry/browser';
import { selectLoadedProjectId } from '@core/state/ui/project/project-ui.selectors';
import { getSendableStudyTableParamsDto } from '../../state/ui/study/study-ui.selectors';
import { studySchema } from '@core/state/entities/study/study.entity';
import { selectCurrentProject } from '@app/core/state/entities/project/project.selectors';
import { StudiesTableHttpService } from '@core/services/study-table/study-table.service';
import {
  ApiException,
  StudyHttpService,
} from '@core/services/api-client.generated';
import {
  IncomingTableParamsDtoOfStudyFilters,
  StudyPdfStatusFilter,
} from '../api-client.generated';
import {
  ProposedPdfFilterRefusal,
  studiesActions,
} from '@core/actions/studies.actions';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { selectStudyListOnScreen } from '@core/state/ui/study/study-ui.selectors';

export const projectStudyIncomingDtoSchema = {
  data: [studySchema],
};

/** The problem type the server answers a refused PDF filter with (#4719, `409`). */
export const PDF_STATUS_FILTER_UNAVAILABLE = 'pdf-status-filter-unavailable';

/** What the reader hears when the server refuses the PDF filter (open question 4, settled). */
export const PDF_STATUS_FILTER_REFUSED_MESSAGE =
  "PDF status isn't available, so the PDF filter is off.";

/**
 * Whether a failed list fetch is the server refusing the PDF filter: the generated client throws the problem document
 * it declares for `409`, or an `ApiException` carrying it.
 */
export function isPdfStatusFilterRefusal(error: unknown): boolean {
  let problem: unknown = error;
  if (ApiException.isApiException(error)) {
    if (error.status !== 409) return false;
    try {
      problem = JSON.parse(error.response);
    } catch {
      return false;
    }
  }
  return (
    typeof problem === 'object' &&
    problem !== null &&
    (problem as { type?: unknown }).type === PDF_STATUS_FILTER_UNAVAILABLE
  );
}

/** Slice 9's problem types for a refused Proposed PDF waiting (`409`). */
export const PDF_PROPOSALS_UNAVAILABLE_PROBLEM_TYPE = 'study-pdf-proposals-unavailable';
export const PDF_PROPOSALS_TOO_MANY_PROBLEM_TYPE = 'study-pdf-proposals-too-many';

/** What the reader hears when the server refuses Proposed PDF waiting (settled 4 for too many). */
export const PROPOSED_PDF_FILTER_REFUSED_MESSAGES: Readonly<
  Record<ProposedPdfFilterRefusal, string | null>
> = {
  unavailable: "Proposed PDFs can't be listed here yet.",
  tooMany: 'There are too many proposed PDFs to list at once.',
  // A reader who can't Set opened a link with it: reset quietly, as the board does.
  forbidden: null,
};

/**
 * Whether a failed list fetch that sent Proposed PDF waiting is the server refusing it (slice 9): its `409`s, or a
 * `403`, which a list request that sent it can only mean for it. Null for anything else.
 */
export function proposedPdfFilterRefusal(
  error: unknown,
  sentProposedPdfWaiting: boolean
): ProposedPdfFilterRefusal | null {
  if (!sentProposedPdfWaiting) return null;
  const status = ApiException.isApiException(error)
    ? error.status
    : (error as { status?: unknown } | null)?.status;
  if (status === 403) return 'forbidden';
  let problem: unknown = error;
  if (ApiException.isApiException(error)) {
    if (error.status !== 409) return null;
    try {
      problem = JSON.parse(error.response);
    } catch {
      return null;
    }
  }
  const type =
    typeof problem === 'object' && problem !== null
      ? (problem as { type?: unknown }).type
      : null;
  return type === PDF_PROPOSALS_UNAVAILABLE_PROBLEM_TYPE
    ? 'unavailable'
    : type === PDF_PROPOSALS_TOO_MANY_PROBLEM_TYPE
      ? 'tooMany'
      : null;
}

export const combineWithNext = <TOriginal, TIncoming>(
  combineWithObservable$: Observable<TIncoming>
) =>
  pipe(
    switchMap((originalItem: TOriginal) =>
      combineLatest([of(originalItem).pipe(take(1)), combineWithObservable$])
    )
  );

@Injectable()
export class StudyTableEffects {
  studyPageData$: Observable<Action> = createEffect(() => {
    return this._actions$.pipe(
      ofType(studiesActions.fetchStudyList, studiesActions.refreshStudyList),
      combineWithNext(
        this._store
          .select(selectLoadedProjectId)
          .pipe(filter((pId): pId is string => !!pId))
      ),
      concatLatestFrom((x) =>
        this._store.select(getSendableStudyTableParamsDto)
      ),
      switchMap(
        ([[, projectId], sendableParams]: [
          [any, string],
          IncomingTableParamsDtoOfStudyFilters,
        ]) => {
          return this._studyService
            .getTableData(projectId, sendableParams)
            .pipe(
              map((pagedStudyDetails) =>
                studiesActions.studyListUpdated(pagedStudyDetails)
              ),
              // The Library shows the failure in the page, with "Try again" (C-12); no snackbar, so there is one
              // message. The effect keeps listening for the next fetch.
              catchError((err: unknown) => {
                // #4719: the PDF filter isn't available after all. Reset it to Any and hide it; the URL rewrite that
                // follows fetches the list once more.
                if (isPdfStatusFilterRefusal(err)) {
                  return of(studiesActions.pdfStatusFilterRefused());
                }
                // Slice 9: the same for Proposed PDF waiting, with its own reasons.
                const proposed = proposedPdfFilterRefusal(
                  err,
                  sendableParams.filters?.pdfStatusFilter ===
                    StudyPdfStatusFilter.ProposedPdfWaiting
                );
                if (proposed) {
                  return of(
                    studiesActions.proposedPdfFilterRefused({ reason: proposed })
                  );
                }
                captureException(
                  `An error occurred while fetching studies. - ${JSON.stringify(err)}`
                );
                return of(studiesActions.studyListLoadFailed());
              })
            );
        }
      )
    );
  });

  /**
   * #4719 criterion 13: the list has no live region of its own, so the refusal is announced politely through the CDK
   * announcer (D16).
   */
  /** Slice 9: a refused Proposed PDF waiting is announced politely, as the PDF filter's refusal is. */
  announceProposedPdfFilterRefused$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(studiesActions.proposedPdfFilterRefused),
        tap(({ reason }) => {
          const message = PROPOSED_PDF_FILTER_REFUSED_MESSAGES[reason];
          if (message) void this._announcer.announce(message, 'polite');
        })
      );
    },
    { dispatch: false }
  );

  announcePdfStatusFilterRefused$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(studiesActions.pdfStatusFilterRefused),
        tap(() => {
          void this._announcer.announce(
            PDF_STATUS_FILTER_REFUSED_MESSAGE,
            'polite'
          );
        })
      );
    },
    { dispatch: false }
  );

  /**
   * Reversible deletion S4a (DL-R1S-10): when a search of the open project is deleted, a study list already on screen
   * is fetched again with its current parameters, so its rows and totals drop the moved Studies without a reload. A
   * list that is not loaded is left alone: opening it fetches afresh.
   */
  refreshAfterSearchDeleted$: Observable<Action> = createEffect(() => {
    return this._actions$.pipe(
      ofType(projectDetailActions.searchDeletedNotificationReceived),
      concatLatestFrom(() => this._store.select(selectStudyListOnScreen)),
      filter(([, onScreen]) => onScreen),
      map(() => studiesActions.refreshStudyList())
    );
  });

  private readonly _announcer = inject(LiveAnnouncer);

  constructor(
    private _store: Store,
    private _actions$: Actions,
    private _studiesTableService: StudiesTableHttpService,
    private _studyService: StudyHttpService
  ) {}
}
